import { AssignmentAuditDetailsResponse, CommandExecutionResponse, MonitoredOperationListItemResponse } from '@6grain/masofa-monolith-api-client/common';
import { formatAssignmentDuration } from './assignment-duration.util';

// TODO: remove this additive field when the generated client includes the monitoring QueuedAt contract.
type TimedOperation = Pick<MonitoredOperationListItemResponse, 'state' | 'startedAt' | 'completedAt' | 'durationSeconds'> & { queuedAt?: string | null };
type OutcomeInput = Pick<MonitoredOperationListItemResponse, 'state' | 'safeOutputJson' | 'operationType'> & Pick<AssignmentAuditDetailsResponse, 'executionDefinitionKey'>;

export function assignmentOutcome(item: OutcomeInput): { label: string; skipped: boolean; reason: string | null } {
  if (item.state !== 'Completed') return { label: item.state || '—', skipped: false, reason: null };
  let output: Record<string, unknown>;
  try {
    const parsed: unknown = JSON.parse(item.safeOutputJson || '{}');
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('Invalid output');
    output = parsed as Record<string, unknown>;
  } catch {
    return { label: 'Completed', skipped: false, reason: null };
  }
  const skipped = output['Skipped'] ?? output['skipped'];
  const reason = output['ResultMessage'] ?? output['resultMessage'];
  const segmentation = (item.operationType ?? item.executionDefinitionKey ?? '').includes('field-segmentation-net');
  return {
    label: skipped === true ? 'ASSIGNMENT_SKIPPED' : skipped === false && segmentation ? 'SEGMENTATION_COMPLETED' : 'Completed',
    skipped: skipped === true,
    reason: skipped === true && typeof reason === 'string' ? reason : null,
  };
}

function instant(value: string | null | undefined): number | null {
  const result = value ? Date.parse(value) : NaN;
  return Number.isFinite(result) ? result : null;
}

export function assignmentTiming(item: TimedOperation, now = Date.now()): { queue: string; processing: string } {
  const queued = instant(item.queuedAt);
  const started = instant(item.startedAt);
  const completed = instant(item.completedAt);
  const queueEnd = item.state === 'Pending' ? now : started;
  const queueSeconds = queued !== null && queueEnd !== null && queueEnd >= queued ? (queueEnd - queued) / 1000 : null;
  const end = completed ?? (item.state === 'Running' || item.state === 'Cancelling' ? now : null);
  const processing = item.state === 'Pending' ? null : started !== null && end !== null && end >= started ? (end - started) / 1000 : item.durationSeconds;
  return { queue: formatAssignmentDuration(queueSeconds), processing: formatAssignmentDuration(processing) };
}

export interface AssignmentStageTiming { label: string; duration: string; recorded: boolean; cached: boolean; }

export function assignmentDetailTiming(item: AssignmentAuditDetailsResponse, now = Date.now()): { queue: string; processing: string } {
  const started = instant(item.startedAt);
  const latestPending = (item.stateHistory ?? []).filter(row => row.state === 'Pending')
    .map(row => instant(row.changedAt)).filter((at): at is number => at !== null && (item.state === 'Pending' || started === null || at <= started))
    .sort((a, b) => b - a)[0];
  return assignmentTiming({ ...item, queuedAt: latestPending === undefined ? null : new Date(latestPending).toISOString() }, now);
}

/** Uses only descendants of the latest pipeline attempt; merges overlapping intervals within each stage. */
export function assignmentStageTimings(executions: readonly CommandExecutionResponse[], now = Date.now(), attempt?: Pick<AssignmentAuditDetailsResponse, 'state' | 'startedAt' | 'stateHistory'>): AssignmentStageTiming[] {
  const boundary = Math.max(instant(attempt?.startedAt) ?? 0, ...(attempt?.stateHistory ?? [])
    .filter(row => row.state === 'Pending').map(row => instant(row.changedAt) ?? 0));
  const root = executions.filter(row => row.stepKey === 'FieldSegmentationNet' && attempt?.state !== 'Pending' && (instant(row.startedAt) ?? 0) >= boundary)
    .sort((a, b) => (instant(b.startedAt) ?? 0) - (instant(a.startedAt) ?? 0) || (b.attempt ?? 0) - (a.attempt ?? 0))[0];
  const ids = new Set<string>(root?.id ? [root.id] : []);
  let added: boolean;
  do {
    added = false;
    for (const row of executions) {
      if (row.id && row.causationId && ids.has(row.causationId) && !ids.has(row.id)) { ids.add(row.id); added = true; }
    }
  } while (added);
  return [
    ['STAGE_DOWNLOAD', 'Download.'], ['STAGE_EXTRACT', 'Extract.'], ['STAGE_QC', 'Qc.'],
    ['STAGE_MASKS', 'LoadMasks'], ['STAGE_INFERENCE', 'Infer'],
  ].map(([label, key]) => {
    const rows = executions.filter(row => row.id && ids.has(row.id) && (key.endsWith('.')
      ? row.stepKey?.startsWith(`FieldSegmentationNet.${key}`) : row.stepKey === `FieldSegmentationNet.${key}`));
    const cached = rows.some(row => {
      if (row.state !== 'Succeeded') return false;
      try { return (JSON.parse(row.inputSummaryJson || '{}') as { cacheHit?: unknown })?.cacheHit === true; }
      catch { return false; }
    });
    const intervals = rows.map(row => [instant(row.startedAt), instant(row.completedAt) ?? (row.state === 'Running' ? now : null)])
      .filter((pair): pair is [number, number] => pair[0] !== null && pair[1] !== null && pair[1] >= pair[0])
      .sort((a, b) => a[0] - b[0]);
    let milliseconds = 0;
    let previousEnd = -Infinity;
    for (const [start, end] of intervals) { milliseconds += Math.max(0, end - Math.max(start, previousEnd)); previousEnd = Math.max(previousEnd, end); }
    return { label, cached, recorded: intervals.length > 0, duration: formatAssignmentDuration(intervals.length ? milliseconds / 1000 : null) };
  });
}
